import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./InvoiceItem.css";
import {
  Plus,
  Trash2,
  Save as SaveIcon,
  Info,
  Percent as PercentIcon,
  FileText,
} from "lucide-react";

const InvoiceItem = () => {
  const { companyId } = useCompany();
  const [rows, setRows] = useState([]);

  // 1️⃣ Load existing items (UNCHANGED)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data: dbRows, error } = await supabase
        .from("invoice_items")
        .select("id, sn, description, amount")
        .eq("company_id", companyId);
      if (error) {
        console.error("Fetch error:", error);
      } else if (dbRows?.length) {
        setRows(dbRows);
      } else {
        setRows([{ id: null, sn: 1, description: "", amount: "" }]);
      }
    })();
  }, [companyId]);

  // 2️⃣ Persist UI state to localStorage (UNCHANGED)
  useEffect(() => {
    localStorage.setItem("invoiceRows", JSON.stringify(rows));
  }, [rows]);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: null, sn: prev.length + 1, description: "", amount: "" },
    ]);
  };

  const handleInputChange = (idx, field, value) => {
    const updated = [...rows];
    updated[idx][field] = value;
    setRows(updated);
  };

  const deleteRow = async (idx) => {
    if (!companyId) return;
    const toDelete = rows[idx];
    const query = supabase.from("invoice_items").delete();
    if (toDelete.id) query.eq("id", toDelete.id);
    else query.eq("company_id", companyId).eq("sn", toDelete.sn);
    const { error } = await query;
    if (error) console.error("Delete error:", error);
    const updated = rows
      .filter((_, i) => i !== idx)
      .map((r, i) => ({ ...r, sn: i + 1 }));
    setRows(updated);
  };

  // 3️⃣ Save / upsert (UNCHANGED)
  const saveData = async () => {
    if (!companyId) return;
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();
    if (authErr || !user) {
      alert("Please sign in.");
      return;
    }
    const userId = user.id;

    try {
      const payload = rows.map((row) => ({
        id: row.id || uuidv4(),
        company_id: companyId,
        user_id: userId,
        sn: row.sn,
        description: row.description,
        amount: row.amount,
      }));

      const { error: upsertErr } = await supabase
        .from("invoice_items")
        .upsert(payload, { onConflict: ["id"] });
      if (upsertErr) throw upsertErr;

      const snList = rows.map((r) => r.sn).join(",");
      const { error: delErr } = await supabase
        .from("invoice_items")
        .delete()
        .eq("company_id", companyId)
        .not("sn", "in", `(${snList})`);
      if (delErr) throw delErr;

      alert("Saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      alert("Error saving data: " + err.message);
    }
  };

  // —— UI helpers (no logic changes) ——
  // Treat amount as % (0–100). Keep stored value as-is; show a badge for total.
  const totalPct = useMemo(
    () => rows.reduce((sum, r) => sum + (parseFloat(r.amount || 0) || 0), 0),
    [rows]
  );

  const onRowKeyDown = (e, idx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (idx === rows.length - 1) addRow();
    }
  };

  return (
    <div className="inv-items-shell">
      {/* Header */}
      <header className="panel inv-head">
        <div className="title">
          <h2>
            <FileText size={18} /> Invoice Breakdown
          </h2>
          <p className="muted">
            Define your line item templates used when generating
            invoices/estimates.
          </p>
        </div>
        <div className="actions">
          <button className="btn-ghost" onClick={addRow}>
            <Plus size={16} /> Add Item
          </button>
          <button className="btn-primary" onClick={saveData}>
            <SaveIcon size={16} /> Save
          </button>
        </div>
      </header>

      {/* Total indicator */}
      <section className="panel inv-meter">
        <div className="meter-row">
          <span className="label">
            <PercentIcon size={14} /> Total Percentage
          </span>
          <span
            className={`pill ${
              totalPct === 100 ? "ok" : totalPct > 100 ? "warn" : ""
            }`}
            title="Sum of all percentage values"
          >
            {Number.isFinite(totalPct) ? totalPct.toFixed(0) : 0}%
          </span>
        </div>
        <p className="muted tiny">
          <Info size={12} /> Optional: Aim for a total of <strong>100%</strong>{" "}
          if you want the items to represent a complete distribution.
        </p>
      </section>

      {/* Table */}
      <section className="panel table-card">
        <div className="table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th style={{ width: 64 }}>S/N</th>
                <th>Description</th>
                <th style={{ width: 220 }}>Percentage</th>
                <th style={{ width: 100 }} className="center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={`${row.id || "new"}-${idx}`}>
                  <td>{row.sn}</td>
                  <td>
                    <input
                      type="text"
                      placeholder="e.g., Materials, Labour, Logistics"
                      value={row.description}
                      onChange={(e) =>
                        handleInputChange(idx, "description", e.target.value)
                      }
                      onKeyDown={(e) => onRowKeyDown(e, idx)}
                    />
                  </td>
                  <td>
                    <div className="input-suffix">
                      <input
                        type="number"
                        inputMode="decimal"
                        placeholder="0"
                        value={row.amount}
                        onChange={(e) =>
                          handleInputChange(idx, "amount", e.target.value)
                        }
                        onKeyDown={(e) => onRowKeyDown(e, idx)}
                      />
                      <span className="suffix">%</span>
                    </div>
                  </td>
                  <td className="center">
                    <button
                      type="button"
                      className="icon-btn danger"
                      onClick={() => deleteRow(idx)}
                      aria-label={`Delete row ${row.sn}`}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan="4">
                    <div className="empty-state">
                      No items yet. Click <strong>Add Item</strong> to begin.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer actions (duplicate for bottom of page convenience) */}
        <div className="table-actions">
          <button className="btn-ghost" onClick={addRow}>
            <Plus size={16} /> Add Item
          </button>
          <button className="btn-primary" onClick={saveData}>
            <SaveIcon size={16} /> Save
          </button>
        </div>
      </section>
    </div>
  );
};

export default InvoiceItem;
